/**
 * Login page for OMEN Web Tracker.
 * Authenticates with client="tracker"; redirects admin accounts with an
 * explicit French message. Includes a password-reset modal.
 */

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { useAppStore } from '../store/appStore';
import type { AuthUser } from '../types/api';
import styles from './Login.module.css';
import WaveText from '../components/WaveText';

// ── Password reset modal ──────────────────────────────────────────────────────

/**
 * Modal that collects the user's email and calls POST /auth/request-reset.
 * Always shows a success confirmation after submit (server returns 200 regardless).
 * @param onClose - callback to dismiss the modal
 */
function ResetModal({ onClose }: { onClose: () => void }) {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post('/auth/request-reset', { email });
    } catch {
      // Intentionally swallowed — server always returns 200 for security.
    } finally {
      setLoading(false);
      setSubmitted(true);
    }
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <h2 className={styles.modalTitle}>Réinitialisation du mot de passe</h2>

        {submitted ? (
          <>
            <p className={styles.modalConfirm}>
              Si un compte existe pour cette adresse, un e-mail de réinitialisation
              a été envoyé. Vérifiez votre boîte de réception.
            </p>
            <button className={styles.btn} onClick={onClose}>
              Fermer
            </button>
          </>
        ) : (
          <form onSubmit={handleSubmit} className={styles.form}>
            <label className={styles.label} htmlFor="reset-email">
              Adresse e-mail
            </label>
            <input
              id="reset-email"
              type="email"
              className={styles.input}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
            />
            <div className={styles.resetActions}>
              <button
                type="button"
                className={styles.btnSecondary}
                onClick={onClose}
              >Annuler
              </button>
              <button type="submit" className={styles.btn} disabled={loading}>
                {loading ? 'Envoi…' : 'Envoyer'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// ── Login page ────────────────────────────────────────────────────────────────

/**
 * Main login form. POSTs to /login with client="tracker".
 * On success: stores JWT and navigates to /.
 * On 403: shows a dedicated message for admin accounts.
 * On other errors: shows a generic error message.
 */
export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showReset, setShowReset] = useState(false);

  const setAuth = useAppStore((s) => s.setAuth);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const { data } = await axios.post<{ token: string }>('/login', {
        email,
        password,
        client: 'tracker',
      });

      const user = jwtDecode<AuthUser>(data.token);
      setAuth(data.token, user);
      navigate('/', { replace: true });
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 403) {
        setError(
          "Ce compte n'est pas autorisé à accéder au suivi de vol.\nUtilisez l'interface d'administration.",
        );
      } else {
        setError('Identifiants incorrects. Veuillez réessayer.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <img
          src="/tracker/logo.png"
          alt="OMEN logo"
          width={256}
          height={256}
          className={styles.logo}
        />

        <h1 className={styles.appName}>OMEN</h1>
        {/* <h1 className={styles.appName}>
          <WaveText text="OMEN" firstDelayMs={4000} waveDurationMs={400} charDurationMs={500} intervalSec={14} />
        </h1> */}
        {/* <p className={styles.tagline}>A sounding balloon tracking system</p>  */}
        <p className={styles.tagline}>
          <WaveText text="A sounding balloon tracking system" firstDelayMs={4000} waveDurationMs={1200} charDurationMs={400} intervalSec={14} />
        </p>

        <form onSubmit={handleSubmit} className={styles.form}>
          <label className={styles.label} htmlFor="email">
            Email
          </label>
          <input
            id="email"
            type="email"
            className={styles.input}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
          />

          <label className={styles.label} htmlFor="password">
            Mot de passe
          </label>
          <input
            id="password"
            type="password"
            className={styles.input}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete="current-password"
          />

          {error && (
            <p className={styles.error} role="alert">
              {error}
            </p>
          )}

          <button type="submit" className={styles.btn} disabled={loading}>
            {loading ? 'Connexion…' : 'Se connecter'}
          </button>
        </form>

        <button
          type="button"
          className={styles.forgotLink}
          onClick={() => setShowReset(true)}
        >
          Mot de passe oublié&nbsp;?
        </button>

        <Link to="/tour" className={styles.tourLink}>
          Découvrir l'interface →
        </Link>
      </div>

      {showReset && <ResetModal onClose={() => setShowReset(false)} />}
    </div>
  );
}
